import axios from 'axios'
import router from "@/router";
import { Message } from 'element-ui';

// token刷新标志，防止并发刷新
let isRefreshing = false;
// 待重试的请求队列
let pendingRequests = [];

// 创建可一个新的axios对象
const request = axios.create({
    baseURL: process.env.VUE_APP_BASEURL,   // 后端的接口地址  ip:port
    timeout: 30000                          // 30s请求超时
})

/**
 * 刷新token
 */
async function refreshAccessToken() {
    try {
        const user = JSON.parse(localStorage.getItem("xm-user") || '{}');
        if (!user.refreshToken) {
            throw new Error('没有refreshToken');
        }

        // 使用原生axios，避免拦截器循环
        const response = await axios.post(
            `${process.env.VUE_APP_BASEURL}/admins/refreshToken`,
            { refreshToken: user.refreshToken },
            {
                headers: { 'Content-Type': 'application/json;charset=utf-8' },
                timeout: 10000
            }
        );

        if (response.data && response.data.code === '200') {
            const { accessToken, refreshToken } = response.data.data;
            // 更新本地存储的token
            user.accessToken = accessToken;
            user.refreshToken = refreshToken;
            localStorage.setItem("xm-user", JSON.stringify(user));
            console.log('Token刷新成功');
            return accessToken;
        } else {
            throw new Error(response.data?.msg || 'Token刷新失败');
        }
    } catch (error) {
        console.error('刷新token失败:', error);
        // 刷新失败，清除用户信息并跳转登录
        localStorage.removeItem("xm-user");
        router.push('/login');
        throw error;
    }
}

/**
 * 处理待重试的请求队列
 */
function retryPendingRequests(token) {
    pendingRequests.forEach(callback => callback(token));
    pendingRequests = [];
}

// request 拦截器
// 可以自请求发送前对请求做一些处理
// 比如统一加token，对请求参数统一加密
request.interceptors.request.use(config => {
    // 只有在没有明确设置Content-Type时才设置默认值
    if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';        // 设置请求头格式
    }

    try {
        let user = JSON.parse(localStorage.getItem("xm-user") || '{}')  // 获取缓存的用户信息

        // 使用accessToken而不是token
        if (user.accessToken) {
            config.headers['token'] = user.accessToken  // 设置请求头
        }
    } catch (error) {
        console.error('解析用户数据失败:', error)
        // 如果解析失败，清除localStorage并跳转到登录页
        localStorage.removeItem("xm-user")
        router.push('/login')
    }

    return config
}, error => {
    console.error('request error: ' + error) // for debug
    return Promise.reject(error)
});

// response 拦截器
// 可以在接口响应后统一处理结果
request.interceptors.response.use(
    response => {
        let res = response.data;

        // 兼容服务端返回的字符串数据
        if (typeof res === 'string') {
            res = res ? JSON.parse(res) : res
        }

        // 处理token过期或无效的情况
        if (res.code === '401' || res.code === '5008') {
            console.warn('Token已过期或无效，尝试刷新token');

            // 如果正在刷新token，将请求加入队列
            if (isRefreshing) {
                return new Promise((resolve) => {
                    pendingRequests.push((token) => {
                        response.config.headers['token'] = token;
                        resolve(request(response.config));
                    });
                });
            }

            isRefreshing = true;

            // 尝试刷新token
            return refreshAccessToken()
                .then(newToken => {
                    // 更新原请求的token
                    response.config.headers['token'] = newToken;
                    // 重试原请求
                    retryPendingRequests(newToken);
                    return request(response.config);
                })
                .catch(() => {
                    // 刷新失败，跳转登录页
                    Message.error('登录已过期，请重新登录');
                    router.push('/login');
                    return Promise.reject(new Error('Token刷新失败'));
                })
                .finally(() => {
                    isRefreshing = false;
                });
        }

        return res;
    },
    error => {
        console.error('response error: ' + error) // for debug

        // 处理网络错误中的401状态码
        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;

            // 401或token相关错误
            if (status === 401 || data?.code === '401' || data?.code === '5008') {
                console.warn('请求返回401，token已失效');

                // 如果正在刷新token，将请求加入队列
                if (isRefreshing) {
                    return new Promise((resolve) => {
                        pendingRequests.push((token) => {
                            error.config.headers['token'] = token;
                            resolve(request(error.config));
                        });
                    });
                }

                isRefreshing = true;

                // 尝试刷新token
                return refreshAccessToken()
                    .then(newToken => {
                        error.config.headers['token'] = newToken;
                        retryPendingRequests(newToken);
                        return request(error.config);
                    })
                    .catch(() => {
                        Message.error('登录已过期，请重新登录');
                        router.push('/login');
                        return Promise.reject(error);
                    })
                    .finally(() => {
                        isRefreshing = false;
                    });
            }

            // 其他HTTP错误
            if (status === 403) {
                Message.error('没有权限访问该资源');
            } else if (status === 404) {
                Message.error('请求的资源不存在');
            } else if (status === 500) {
                Message.error('服务器内部错误');
            } else if (status === 502 || status === 503) {
                Message.error('服务暂时不可用，请稍后重试');
            }
        } else if (error.code === 'ECONNABORTED') {
            Message.error('请求超时，请检查网络连接');
        } else if (!error.response) {
            Message.error('网络连接失败，请检查网络');
        }

        return Promise.reject(error)
    }
)


export default request